import React, { useState } from 'react';
import { isPropertyAccessOrQualifiedName } from 'typescript';
import Tools from '../../cvp-webgames-core/Misc/Tools';
import { setFocus } from '../../misc/tools';
import GeneralProceduresCricPart9 from '../../scenarios/general/procedures/cric/parts/GeneralProceduresCricPart9';
import { BaseConfig } from '../BaseConfig';
import GeneralButton from '../helpers/GeneralButton';
import GeneralButtonWithText from '../helpers/GeneralButtonWithText';
import { UIProperties } from '../UIProperties';

enum Windows {
	Introduction,
	AssessmentAndOptPriorToMedication,
	AssessmentAndOptAfterMedication,
	AssessmentAndOptFailed,
	AdministeringMedications,
	UsingTheVortex
}

const DebriefUI = (props: UIProperties<DebriefUIConfig>) => {
	const [currentWindow, setCurrentWindow] = useState(Windows.Introduction);
	const [alreadySelectedWindows, setAlreadySelectedWindows] = useState([0]);
	const updateWindow = (window: Windows) => {
		if(alreadySelectedWindows.indexOf(window) === -1) {
			var windows = alreadySelectedWindows;
			windows.push(window);
			setAlreadySelectedWindows(windows);
		}
		setCurrentWindow(window);
	}

	const hasWindowBeenSeen = (window: Windows) => {
		return alreadySelectedWindows.indexOf(window) !== -1;
	}
	const renderCurrentWindow = () => {
		switch(currentWindow) {
			case Windows.Introduction:
				return renderIntroductionWindow();
			case Windows.AssessmentAndOptPriorToMedication:
				return renderAssessmentAndOptPriorToMedication();
			case Windows.AssessmentAndOptAfterMedication:
				return renderAssessmentAndOptAfterMedication();
			case Windows.AssessmentAndOptFailed:
				return renderAssessmentAndOptFailed();
			case Windows.AdministeringMedications:
				return renderAdministeringMedications();
			case Windows.UsingTheVortex:
				return renderUsingTheVortex();
		}

		return (null);
	};

	const renderIntroductionWindow = () => {
		return (
			<div className="modal-disable-select" >
				<p className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}} tabIndex={0} id='introduction-root'>In this scenario, you should have noticed the following:</p>
				<div>
					<ul>
						{props.parameters.intro.items.map((i: string) => {
							return (
							<li key={Tools.generateGuid()}>
								<p tabIndex={0} className='title-font' style={{
									margin: '0',
									fontSize: '18pt',
									fontWeight: 'normal'
								}}>{i}</p>
							</li>)
						})}
					</ul>
				</div>
				<p className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}} tabIndex={0}>{GeneralProceduresCricPart9.isYesClicked ? 'In addition to reviewing detailed feedback about your performance during the scenario, you can also review a video that shows physicians performing a cricothyrotomy using the Vortex approach.'  : 'In addition to reviewing detailed feedback about your performance during the scenario, you can also review a video that shows physicians managing this patient\'s airway using the Vortex approach and all airway devices.'}</p>
				{GeneralProceduresCricPart9.isYesClicked ? (
					<div style={{
						position: 'absolute',
						width: '100%',
						height: '604px',
						pointerEvents: 'all'
					}}>
						<video tabIndex={0} style={{
							width: '70%',
							position: 'absolute',
							left: '50%',
							top: '50%',
							transform: 'translate(-50%, -50%)'
						}} src="./assets/videos/daarc-lite-debrief-cric.mp4" playsInline={true} controls={true}></video>
					</div>
				) : (<GeneralButtonWithText parameters= {{
					text: 'Link',
					ariaLbl: 'Link to an explanation video',
					top: '0',
					left: '0',
					width: '130px',
					isDisabled: false,
					srcIdle: "./assets/images/main-menu/button_menu_exit.png",
					srcHighlight: "./assets/images/main-menu/button_menu_exit_down.png",
					srcDisabled: null,
					customImgStyle: {
					},
					customButtonStyle : {
					},
					customTextStyle: {
						fontSize: '12pt'
					},
					customMainStyle: {
						position: 'relative',
						left: '50%',
						transform: 'translate(-50%, 0)'
					},
					onClicked: () => {
						window.open(props.parameters.intro.link, '_blank');
					}
				}}/>)}
			</div>
		)
	}
	const renderAssessmentAndOptPriorToMedication = () => {
		var arrToMap: string[] = null;
		if(props.parameters.isEmanuelMcKenzie) {
			arrToMap = [
				'Adjusted bed height',
				'Positioned patient',
				'You were informed during SBAR that the respiratory therapist had already completed two optimizations at BVM ventilation'
			]
		} else {
			arrToMap = props.parameters.priorToMedsItems;
		}
		return (
			<div>
				<p tabIndex={0} id='prior-root' className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}}>{props.parameters.isEmanuelMcKenzie ? 'The following actions had already been taken when you entered the room:' : 'You performed the following actions prior to administering medications, which can help optimize your attempts at managing the difficult airway.'}</p>
				<div>
					<ul>
						{arrToMap.map((i: string) => {
							return (
							<li key={Tools.generateGuid()}>
								<p tabIndex={0} className='title-font' style={{
									margin: '0',
									fontSize: '18pt',
									fontWeight: 'normal'
								}}>{i}</p>
							</li>)
						})}
					</ul>
				</div>
			</div>
		)
	}

	const renderAssessmentAndOptAfterMedication = () => {
		var arrToMap: string[] = null;
		if(props.parameters.isEmanuelMcKenzie) {
			arrToMap = [
				'During cardiac arrest, pre-oxygenation is not relevant. Additionally, the patient is already in the supine position.'
			]
		} else {
			arrToMap = props.parameters.afterMedsItems;
		}
		return (
			<div>
				<p className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}} tabIndex={0} id='after-root'>{props.parameters.isEmanuelMcKenzie ? 'You performed or attempted to perform the following actions before making additional attempts at managing the airway' : 'You performed the following actions but did so after medication was administered or during an attempt, which wasn’t optimal. Completing these optimizations before the patient becomes apneic will buy you more time in your attempts to manage the airway.'}</p>
				<div>
					<ul>
						{props.parameters.afterMedsItems.map((i: string) => {
							return (
							<li  key={Tools.generateGuid()}>
								<p tabIndex={0} className='title-font' style={{
									margin: '0',
									fontSize: '18pt',
									fontWeight: 'normal'
								}}>{i}</p>
							</li>)
						})}
					</ul>
				</div>
			</div>
		)
	}

	const renderAssessmentAndOptFailed = () => {
		return (
			<div>
				<p className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}} tabIndex={0} id='failed-root'>You failed to perform the following actions during the scenario, which wasn’t optimal. These optimizations are an important part of managing a difficult airway:</p>
				<div>
					<ul>
						{props.parameters.failedItems.map((i: string) => {
							return (
							<li key={Tools.generateGuid()}>
								<p tabIndex={0} className='title-font' style={{
									margin: '0',
									fontSize: '18pt',
									fontWeight: 'normal'
								}}>{i}</p>
							</li>)
						})}
					</ul>
				</div>
			</div>
		)
	}

	const renderAdministeringMedications = () => {
		return (
			<div tabIndex={0} id='admin-root'>
				<p className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}} tabIndex={0}>{props.parameters.administeringMedsTex}</p>
			</div>
		)
	}

	const renderUsingTheVortex = () => {
		return (
			<div>
				<p className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}} tabIndex={0} id='vortex-root'>You successfully managed the airway using the following attempts, as per the Vortex:</p>
				<div>
					<ul>
						{props.parameters.vortex.successItems.map((i: string) => {
							return (
							<li key={Tools.generateGuid()}>
								<p className='title-font' style={{
									margin: '0',
									fontSize: '18pt',
									fontWeight: 'normal'
								}} tabIndex={0}>{i}</p>
							</li>)
						})}
					</ul>
				</div>

				<p className='title-font' style={{
					margin: '0',
					fontSize: '24pt',
					fontWeight: 'normal'
				}} tabIndex={0}>Before your next attempt, ask yourself:</p>
				<div>
					<ul>
						{props.parameters.vortex.askYourselfItems.map((i: string) => {
							return (
							<li key={Tools.generateGuid()}>
								<p tabIndex={0} className='title-font' style={{
									margin: '0',
									fontSize: '18pt',
									fontWeight: 'normal'
								}}>{i}</p>
							</li>)
						})}
					</ul>
				</div>
			</div>
		)
	}
	return (
		<div style={{
			position: 'absolute',
			top: '0',
			left: '0',
			width: '100%',
			height: '100%'
		}}>
			<div style={{
				position: 'absolute',
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)',
				width: '1920px',
				height: '1080px'
			}}>
				<img style={{
					width: '100%',
					height: '90px'
				}} src="./assets/images/SBAR/header_modal_1.png"/>
				<div style={{
					position: 'absolute',
					top: '01px',
					left: '3px',
					width: '1822px',
					height: '85px',
				}}>
					<p style={{
						margin: '0px',
						fontSize: '45pt',
						textAlign: 'center',
						position: 'relative',
						top: '10px'
					}} className="title-font" tabIndex={0} id='debrief-ui-top'>Debrief</p>
				</div>
				<GeneralButton parameters= {{
					top: '7px',
					left: '1819px',
					width: '90px',
					ariaLbl: 'close',
					srcIdle: "./assets/images/general/buttons/button_close_window_main.png",
					srcHighlight: "./assets/images/general/buttons/button_close_window_down.png",
					srcDisabled: null,
					highlightOnHover: false,
					onClicked: () => {
						props.parameters.onCloseClicked();
					},
					customBttnStyle: {
						width: '100%'
					},
					customImgStyle: {
						width: '100%'
					}
				}}/>
				<div style={{
					width: '100%',
					height: '990px',
					position: 'absolute',
					top: '90px',
					left: '0'
				}}>
					<div style={{
						border: '3px solid rgb(192, 161, 6)',
						backgroundColor: 'rgb(36, 36, 36)',
						position: 'absolute',
						left: '4px',
						top: '0',
						width: '410px',
						height: '100%'
					}}>
						<CustomDebriefButton 
							showYellowThingyOnTheSide={currentWindow === Windows.Introduction}
							showCheckmark={hasWindowBeenSeen(Windows.Introduction)}
							width= '380px'
							text='Introduction'
							position= {{
								type: 'absolute', 
								top: '11px',
								left: '10px'
							}}
							onClicked={() => {
								setTimeout(() => {
									setFocus('introduction-root');
								}, 500);
								updateWindow(Windows.Introduction);
							}}/>

						<CustomDebriefButton 
							showYellowThingyOnTheSide={currentWindow === Windows.AssessmentAndOptAfterMedication || 
								currentWindow === Windows.AssessmentAndOptFailed || currentWindow === Windows.AssessmentAndOptPriorToMedication}
							showCheckmark={hasWindowBeenSeen(Windows.AssessmentAndOptAfterMedication) ||
								hasWindowBeenSeen(Windows.AssessmentAndOptFailed) || hasWindowBeenSeen(Windows.AssessmentAndOptPriorToMedication)}
							width= '380px'
							text='Assessment &amp; Optimization'
							position= {{
								type: 'absolute', 
								top: '150px',
								left: '10px'
							}}
							onClicked={() => {
								setTimeout(() => {
									setFocus('assessment-root');
								}, 500);
								updateWindow(Windows.AssessmentAndOptPriorToMedication);
							}}/>

						<CustomDebriefButton 
							showYellowThingyOnTheSide={currentWindow === Windows.AssessmentAndOptPriorToMedication}
							showCheckmark={hasWindowBeenSeen(Windows.AssessmentAndOptPriorToMedication)}
							width= '330px'
							text= { props.parameters.isEmanuelMcKenzie ? 'Prior To Scenario' : 'Prior To Medication' }
							isSub={true}
							position= {{
								type: 'absolute', 
								top: '289px',
								left: '60px'
							}}
							onClicked={() => {
								setTimeout(() => {
									setFocus('prior-root');
								}, 500);
								updateWindow(Windows.AssessmentAndOptPriorToMedication);
							}}/>

						<CustomDebriefButton 
							showYellowThingyOnTheSide={currentWindow === Windows.AssessmentAndOptAfterMedication}
							showCheckmark={hasWindowBeenSeen(Windows.AssessmentAndOptAfterMedication)}
							width= '330px'
							text= { props.parameters.isEmanuelMcKenzie ? 'Before Attempts' : 'After Medication' }
							isSub={true}
							position= {{
								type: 'absolute', 
								top: '396px',
								left: '60px'
							}}
							onClicked={() => {
								setTimeout(() => {
									setFocus('after-root');
								}, 500);
								updateWindow(Windows.AssessmentAndOptAfterMedication);
							}}/>

						<CustomDebriefButton 
							showYellowThingyOnTheSide={currentWindow === Windows.AssessmentAndOptFailed}
							showCheckmark={hasWindowBeenSeen(Windows.AssessmentAndOptFailed)}
							width= '330px'
							text='Failed'
							isSub={true}
							position= {{
								type: 'absolute', 
								top: '503px',
								left: '60px'
							}}
							onClicked={() => {
								setTimeout(() => {
									setFocus('failed-root');
								}, 500);
								updateWindow(Windows.AssessmentAndOptFailed);
							}}/>

						<CustomDebriefButton 
							showYellowThingyOnTheSide={currentWindow === Windows.AdministeringMedications}
							showCheckmark={hasWindowBeenSeen(Windows.AdministeringMedications)}
							width= '380px'
							text='Administering Medications'
							position= {{
								type: 'absolute', 
								top: '612px',
								left: '10px'
							}}
							onClicked={() => {
								setTimeout(() => {
									setFocus('admin-root');
								}, 500);
								updateWindow(Windows.AdministeringMedications);
							}}/>

						<CustomDebriefButton 
							showYellowThingyOnTheSide={currentWindow === Windows.UsingTheVortex}
							showCheckmark={hasWindowBeenSeen(Windows.UsingTheVortex)}
							width= '380px'
							text='Using The Vortex'
							position= {{
								type: 'absolute', 
								top: '751px',
								left: '10px'
							}}
							onClicked={() => {
								setTimeout(() => {
									setFocus('vortex-root');
								}, 500);
								updateWindow(Windows.UsingTheVortex);
							}}/>
					</div>

					<div style={{
						border: '3px solid rgb(192, 161, 6)',
						backgroundColor: 'rgb(36, 36, 36)',
						position: 'absolute',
						left: '417px',
						top: '0',
						width: '1495px',
						height: '100%'
					}}>
						<div style={{
							width: '1468px',
							position: 'absolute',
							left: '10px',
							top: '10px',
							height: '970px'
						}}>
							{renderCurrentWindow()}
						</div>
					</div>
					
					<div tabIndex={0} aria-label="end of u i" id='debrief-ui-bottom'></div>
				</div>
				
			</div>
		</div>
	);

};

const CustomDebriefButton = (props: CustomDebriefButtonConfig) => {
	return (
		<div style={{
			position: props.position.type ? props.position.type : 'relative',
			top: props.position.top,
			left: props.position.left,
			width: props.width,
			pointerEvents: 'none'
		}}>
			<div style={{
				position: 'relative'
			}}>
				<GeneralButtonWithText parameters= {{
					text: '',
					ariaLbl: props.text,
					top: '0px',
					left: '0px',
					isDisabled: false,
					width: props.width,
					srcIdle: props.isSub ? './assets/images/main-menu/button_menu_exit.png' : './assets/images/general/buttons/button_side_large-1.png',
					srcHighlight: null,
					srcDisabled: props.isSub ? './assets/images/main-menu/button_menu_exit.png' : './assets/images/general/buttons/button_side_large-1.png',
					customMainStyle: {
						position: 'relative',
					},
					customImgStyle: {
						transform: 'scaleX(-1)',
						position: 'relative',
						left: '15px',
						width: props.isSub ? '290px' : '340px',
						height: '100%'
					},
					customButtonStyle: {
						height: props.isSub ? '95px' : '128px',
						width: '100%'
					},
					onClicked: () => {
						if(props.onClicked) {
							props.onClicked();
						}
					}
				}}/>

				{props.showCheckmark && props.isSub ? <img style={{
					position: 'absolute',
					top: '9px',
					right: '16px',
					width: '56px',
					height: '76px',
					pointerEvents: 'none',
				}} src="./assets/images/general/button_debrief_2_check.png" /> : (null)}

				{props.showCheckmark && !props.isSub ? <img style={{
					position: 'absolute',
					top: '0px',
					right: '8px',
					width: '66px',
					height: '123px',
					pointerEvents: 'none',
				}} src="./assets/images/main-menu/scenario/check_menu_scenario.png" /> : (null)}
				
				<div style={{
					position: 'absolute',
					height: '100%',
					pointerEvents: 'none',
					width: '100%',
					left: '0',
					top: '0'
				}}>
					<div style={{
						position: 'relative',
						top: '50%',
						left: (() => {
							return props.showCheckmark ? '52px' : '32px';
						})(),
						width: (() => {
							if(props.isSub) {
								return props.showCheckmark ? '205px' : '295px';
							}
							return props.showCheckmark ? '255px' : '345px';
						})(),
						transform: 'translate(0, -50%)'
					}}>
						<p className='title-font' style={{
							textAlign: 'center',
							margin: '0',
							fontSize: props.isSub ? '18pt' : '24pt',
							fontWeight: 'normal',
							...props.textStyle
						}}>{props.text}</p>
					</div>
					
				</div>

				{props.showYellowThingyOnTheSide ? (
					<img style={{
						position: 'absolute',
						top: '1px',
						left: props.isSub ? '10px' : '0px',
						width: props.isSub ? '26px' : '35px',
						pointerEvents: 'none',
						transform: 'scaleX(-1)'
					}} src="./assets/images/general/buttons/button_side_pointer.png" />
				) : (null)}
			</div>
		</div>
	);

};

interface CustomDebriefButtonConfig {
	text: string;
	textStyle?: any;
	width: string;
	position: CustomDebriefPosition;
	isSub?: boolean;
	showYellowThingyOnTheSide?: boolean;
	showCheckmark?: boolean;
	onClicked: () => void;
}

interface CustomDebriefPosition {
	type?: any;
	left: string;
	top: string;
}

export interface DebriefUIConfig extends BaseConfig{
	intro: DebriefIntro
	priorToMedsItems: string[];
	afterMedsItems: string[];
	failedItems: string[];
	administeringMedsTex: string;
	vortex: DebriefVortex;
	isEmanuelMcKenzie?: boolean;
	onCloseClicked: () => void;
}

export interface DebriefIntro {
	items: string[];
	link: string;
}

export interface DebriefVortex {
	successItems: string[];
	askYourselfItems: string[];
}

export default DebriefUI;